import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ImportLink from '../components/import/ImportLink';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import RecordAutocomplete from '../components/RecordAutocomplete';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import QRCodeLabel from '../components/QRCodeLabel';
import CreatedCodes from '../components/CreatedCodes';
import ReferenceSelect from '../components/ReferenceSelect';
import { uploadDnaFile } from '../utils/uploadDnaFile';
import { resolvePhotoUrl } from '../utils/photoUrl';
import ReferencePhylogenyPanel from '../components/ReferencePhylogenyPanel';
import DnaIdentificationsPanel, { LatestIdentification, latestSummary } from '../components/dna/DnaIdentificationsPanel';
import { STORAGE_UNITS, describeLocation, shortLocation } from '../components/specimens/StoragePanel';
import FieldError, { InvalidWithin, errorIdFor, invalidBorder, invalidProps } from '../components/FieldError';
import { collectErrors, hasErrors, required, useFieldErrors, validationMessage } from '../utils/formValidation';
import OrganicEmptyState from '../components/organic/OrganicEmptyState.jsx';
import OrganicLoader from '../components/organic/OrganicLoader.jsx';
import useFocusOnOpen from '../utils/useFocusOnOpen';

// The extract itself and where its tube is kept (migration 0050; routes/dna.py
// EXTRACT_FIELDS). Unlike the fields of EMPTY_EDIT, an edit sends one of
// these only when it changed: update_dna writes whatever the body names, null
// included, and a place it is sent is a place it checks is free.
const EXTRACT_FIELDS = ['extraction_kit', 'extraction_lot', 'concentration_ng_ul', 'a260_280',
  'storage_unit', 'storage_unit_label', 'storage_rack', 'storage_box', 'storage_well'];
// The parts of a place inside a unit: nothing without one.
const PLACE_PARTS = ['storage_unit_label', 'storage_rack', 'storage_box', 'storage_well'];
const EMPTY_EXTRACT = Object.fromEntries(EXTRACT_FIELDS.map(f => [f, '']));
// Refusals of those details that the page says in its own words (errors.*);
// any other keeps the server's.
const EXTRACT_ERRORS = ['well_occupied', 'invalid_storage', 'invalid_text', 'invalid_concentration',
  'invalid_a260_280', 'invalid_platform', 'invalid_barcode'];
// What a run was sequenced on (dna_sequencing_runs.PLATFORMS).
const PLATFORMS = ['sanger', 'nanopore', 'illumina', 'pacbio', 'other'];
// What each file picker offers first: a trace or reads, then the edited
// sequence. A suggestion to the picker, not a rule -- the server takes any
// file, and keeps a .fastq.gz whole (routes/dna_files.py::_extension).
const RAW_FILE_ACCEPT = '.ab1,.scf,.fastq,.fq,.gz,.fasta,.fa,.seq';
const PROCESSED_FILE_ACCEPT = '.fasta,.fa,.fas,.fna,.seq,.txt,.gb,.gbk,.gz';

const blankToNull = (v) => (v === undefined || v === null || String(v).trim() === '' ? null : String(v).trim());

/** The extract's details as a body sends them: blank is null, and so is
 *  every part of a place outside a unit (a well is a place only inside one). */
const extractBody = (values) => {
  const body = Object.fromEntries(EXTRACT_FIELDS.map(f => [f, blankToNull(values[f])]));
  if (!body.storage_unit) PLACE_PARTS.forEach((f) => { body[f] = null; });
  return body;
};

/** Only the details an edit changed, measured against the record as listed. */
const changedExtract = (values, rec) => Object.fromEntries(Object.entries(extractBody(values))
  .filter(([f, v]) => v !== blankToNull(rec[f])));

/** A refusal in the reader's language where the page has words for it. */
const extractErrorMessage = (data, t, fallback) => (EXTRACT_ERRORS.includes(data.code)
  ? t(`errors.${data.code}`, { holder: data.holder })
  : (data.error || fallback));

const EMPTY_FORM = {
  sample_id: '',
  external_id: '',
  slant_id: '',
  plate_id: '',
  performed_by: '',
  laboratory: '',
  date_performed: '',
  notes: '',
  donor_name: '',
  donor_contact: '',
  received_date: '',
  ...EMPTY_EXTRACT,
};

// Same fields as EMPTY_FORM minus the sample/external/slant linkage — those are
// deliberately not editable (see routes/dna.py::update_dna's EDITABLE_FIELDS comment)
// -- and minus EMPTY_EXTRACT, which an edit sends only where it changed.
const EMPTY_EDIT = {
  performed_by: '',
  laboratory: '',
  date_performed: '',
  notes: '',
  donor_name: '',
  donor_contact: '',
  received_date: '',
};

export default function DNA() {
  const { t } = useTranslation(['dna', 'common']);
  // The storage units' names are the specimens' (one list, utils.STORAGE_UNITS).
  const { t: tSpec } = useTranslation('specimens');
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // internal_expert is intentionally excluded — spec: "No edit access to lab data (view only)"
  const canEdit = user?.roles?.some(r => ['collection_manager', 'lab_technician', 'lab_manager'].includes(r));
  const canDelete = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));
  const canAssign = user?.roles?.some(r => ['collection_manager', 'lab_manager'].includes(r));
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  // The form takes focus as it opens: its opener may be the empty state,
  // which goes away while the form is open.
  const isolateHeadingRef = useFocusOnOpen(showForm);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [sourceFilled, setSourceFilled] = useState(false); // sample/external were auto-filled by the slant/plate picker (vs chosen by hand)
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState('');
  const [userList, setUserList]     = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [editTarget, setEditTarget]   = useState(null); // { id, form }
  const [editSaving, setEditSaving]   = useState(false);
  const [editError, setEditError]     = useState('');
  const [dnaAssignments, setDnaAssignments]         = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [allUsers, setAllUsers]                     = useState([]);
  const [assignUserId, setAssignUserId]             = useState('');
  const [assignError, setAssignError]               = useState('');
  const [assignSaving, setAssignSaving]             = useState(false);
  const [newRunTarget, setNewRunTarget] = useState(null); // { dnaId, isolateCode } | null
  const [createdCodes, setCreatedCodes] = useState([]);
  const [runs, setRuns]                 = useState([]);
  const [runsLoading, setRunsLoading]   = useState(true);
  const [runForm, setRunForm]           = useState(null); // { dnaId, isolateCode, runId, values, rawFile, processedFile } | null
  // The edit and run dialogs take focus as they open.
  const editHeadingRef = useFocusOnOpen(!!editTarget);
  const runHeadingRef = useFocusOnOpen(!!runForm);
  const [runSaving, setRunSaving]       = useState(false);
  const [runError, setRunError]         = useState('');
  const [flaggingRunId, setFlaggingRunId] = useState(null);
  const [qrView, setQrView] = useState(null); // a code string to show as a QR label, or null
  const [idsFor, setIdsFor] = useState(null); // the isolate whose identifications (PITAX, 0051) are open, or null
  const [highlight, setHighlight] = useState(null); // { runIds: [run_id], dnaId } from a scan / deep link, or null
  const [navNotFound, setNavNotFound] = useState(false);
  const rowRefs = useRef({}); // 'run:<run_id>' / 'dna:<dna_id>' -> DOM node
  const [geneOptions, setGeneOptions] = useState([]); // [{id, name}], for resolving the selected gene's name at run-submit time (S3 key subfolder)
  // Field-level errors for the three forms: a new isolate, an isolate's
  // edit, and a sequencing run.
  const createErrs = useFieldErrors();
  const editErrs = useFieldErrors();
  const runErrs = useFieldErrors();

  const load = () => {
    setLoading(true);
    api.get('/dna')
      .then(res => setRecords(res.data))
      .catch(err => setError(err.response?.data?.error || t('failedToLoad')))
      .finally(() => setLoading(false));
    setRunsLoading(true);
    api.get('/dna/runs')
      .then(res => setRuns(res.data))
      .catch(() => {})
      .finally(() => setRunsLoading(false));
  };

  // The isolate list again, quietly: an identification withdrawn in the
  // panel may change the line each isolate shows (latest_identification).
  const refreshRecords = () => {
    api.get('/dna').then(res => setRecords(res.data)).catch(() => {});
  };

  // Closing the panel removes the focused Close button: focus goes back to
  // the isolate's own button, not to the top of the page.
  const closeIdentifications = () => {
    const opener = idsFor && document.querySelector(`[aria-controls="dna-identifications-${idsFor}"]`);
    setIdsFor(null);
    if (opener) opener.focus();
  };

  const confirmDeleteRecord = () => {
    setDeletingRecord(true);
    setDeleteError('');
    api.delete(`/dna/${deleteTarget}`)
      .then(() => { if (idsFor === deleteTarget) setIdsFor(null); setDeleteTarget(null); load(); })
      .catch(err => setDeleteError(err.response?.data?.error || t('deleteFailed')))
      .finally(() => setDeletingRecord(false));
  };

  useEffect(() => {
    load();
    api.get('/users/names').then(res => setUserList(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/dna/genes').then(res => setGeneOptions(res.data)).catch(() => {});
  }, []);

  useEffect(() => { setTableExpanded(!showForm); }, [showForm]);

  // Arrived via a QR lookup / notification deep link — highlight that run (or all runs of
  // that isolate), once the lists have loaded, then clear the nav state so a later
  // back/forward doesn't re-trigger it.
  useEffect(() => {
    const openRunId = location.state?.openRunId;
    const openDnaId = location.state?.openDnaId;
    if (!openRunId && !openDnaId) return;
    if (loading || runsLoading) return;
    let next = null;
    if (openRunId) {
      const run = runs.find(r => r.run_id === openRunId);
      if (run) next = { runIds: [run.run_id], dnaId: null };
    } else {
      const runIds = runs.filter(r => r.dna_id === openDnaId).map(r => r.run_id);
      if (runIds.length > 0) next = { runIds, dnaId: null };
      else if (records.some(r => r.dna_id === openDnaId)) next = { runIds: [], dnaId: openDnaId };
    }
    if (next) {
      setNavNotFound(false);
      setShowForm(false);
      setTableExpanded(true);
      setHighlight(next);
    } else {
      setHighlight(null);
      setNavNotFound(true);
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading, runsLoading]);

  // Scroll the first highlighted row/chip into view once it has rendered.
  useEffect(() => {
    if (!highlight) return;
    const key = highlight.runIds.length > 0 ? `run:${highlight.runIds[0]}` : `dna:${highlight.dnaId}`;
    const el = rowRefs.current[key];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [highlight]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    createErrs.clear(name);
  };

  const dnaExistsForSlant = !!(form.slant_id && records.some(r => r.slant_id === form.slant_id));
  const dnaExistsForPlate = !!(form.plate_id && records.some(r => r.plate_id === form.plate_id));
  const dnaBlocked = dnaExistsForSlant || dnaExistsForPlate;
  // DNA is extracted from a culture: a plate or a slant, whose specimen or
  // donor record it inherits. It never originates a line (POST /dna 400s).
  const hasCulture = !!(form.slant_id || form.plate_id);

  const handleSubmit = (e) => {
    e.preventDefault();
    // What POST /dna requires (routes/dna.py): the extraction date, and the
    // plate or slant it was extracted from. Each is named beside its field.
    const errors = collectErrors([
      ['date_performed', required(form.date_performed, 'isolateForm.validationError1')],
      ['culture', hasCulture ? null : { code: 'required', key: 'isolateForm.validationError2' }],
    ]);
    if (hasErrors(errors)) {
      setFormError('');
      createErrs.show(errors);
      return;
    }
    createErrs.reset();
    if (dnaExistsForSlant) {
      setFormError(t('isolateForm.dnaExistsForSlantError'));
      return;
    }
    if (dnaExistsForPlate) {
      setFormError(t('isolateForm.dnaExistsForPlateError'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    api.post('/dna', {
      ...form,
      sample_id:    form.sample_id    || null,
      external_id:  form.external_id  || null,
      slant_id:     form.slant_id     || null,
      plate_id:     form.plate_id     || null,
      performed_by: form.performed_by || user?.user_id || null,
      ...extractBody(form),
    })
      .then(res => {
        setShowForm(false);
        setForm({ ...EMPTY_FORM });
        setSourceFilled(false);
        load();
        setNewRunTarget({ dnaId: res.data.dna_id, isolateCode: res.data.isolate_code });
        setCreatedCodes(res.data.isolate_code ? [res.data.isolate_code] : []);
      })
      .catch(err => {
        const data = err.response?.data || {};
        // The slant's or plate's own code is already another isolate's. Named,
        // because the fix is a person checking the tubes, not a retry.
        setFormError(data.code === 'isolate_code_taken'
          ? t('isolateForm.codeTaken', { code: data.isolate_code })
          : extractErrorMessage(data, t, t('isolateForm.createFailed')));
      })
      .finally(() => setSubmitting(false));
  };

  const openEdit = (rec) => {
    setEditTarget({
      id: rec.dna_id,
      rec,   // what changedExtract measures the edit against
      form: {
        ...EMPTY_EDIT,
        ...Object.fromEntries(Object.keys(EMPTY_EDIT).map(k => [k, (rec[k] ?? '').toString().slice(0, k.includes('date') ? 10 : undefined)])),
        ...Object.fromEntries(EXTRACT_FIELDS.map(k => [k, (rec[k] ?? '').toString()])),
      },
    });
    setEditError('');
    editErrs.reset();
    setDnaAssignments([]);
    setAssignUserId('');
    setAssignError('');
    if (canAssign) {
      fetchDnaAssignments(rec.dna_id);
      if (allUsers.length === 0) {
        api.get('/users/names').then(res => setAllUsers(res.data)).catch(() => {});
      }
    }
  };

  const fetchDnaAssignments = (dnaId) => {
    setAssignmentsLoading(true);
    api.get(`/dna/${dnaId}/assignments`)
      .then(res => setDnaAssignments(res.data))
      .catch(() => {})
      .finally(() => setAssignmentsLoading(false));
  };

  const submitDnaAssignment = () => {
    if (!assignUserId || !editTarget) return;
    setAssignSaving(true);
    setAssignError('');
    api.post(`/dna/${editTarget.id}/assignments`, { user_id: assignUserId })
      .then(() => { setAssignUserId(''); fetchDnaAssignments(editTarget.id); })
      .catch(err => setAssignError(err.response?.data?.error || t('common:saveFailed')))
      .finally(() => setAssignSaving(false));
  };

  const removeDnaAssignment = (assignmentId) => {
    api.delete(`/lab-assignments/${assignmentId}`)
      .then(() => setDnaAssignments(l => l.filter(a => a.assignment_id !== assignmentId)))
      .catch(() => {});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditTarget(prev => ({ ...prev, form: { ...prev.form, [name]: value } }));
    editErrs.clear(name);
  };

  const submitEdit = (e) => {
    e.preventDefault();
    // dna.date_performed is NOT NULL, and update_dna's COALESCE quietly keeps
    // the old date when a blank one is sent -- so a cleared date would look
    // saved and not be. Said beside the date instead.
    const errors = collectErrors([
      ['date_performed', required(editTarget.form.date_performed, 'isolateForm.validationError1')]]);
    if (hasErrors(errors)) {
      setEditError('');
      editErrs.show(errors);
      return;
    }
    editErrs.reset();
    setEditSaving(true);
    setEditError('');
    const f = editTarget.form;
    api.put(`/dna/${editTarget.id}`, {
      ...Object.fromEntries(Object.keys(EMPTY_EDIT).map(k => [k, f[k]])),
      date_performed: f.date_performed || null,
      received_date:  f.received_date  || null,
      performed_by:   f.performed_by   || null,
      ...changedExtract(f, editTarget.rec),
    })
      .then(() => { setEditTarget(null); load(); })
      .catch(err => setEditError(extractErrorMessage(err.response?.data || {}, t, t('editModal.updateFailed'))))
      .finally(() => setEditSaving(false));
  };

  const EMPTY_RUN_VALUES = {
    amplification_gene_id: '', amplification_primers_id: '', sequencing_primers_id: '',
    date_sent_for_sequencing: '', sequencing_provider_id: '',
    sequence_quality_id: '', sequence_length: '',
    identification_level_id: '', identification_confidence_id: '',
    identification_blast_result: '', query_coverage: '', notes: '',
    platform: '', barcode: '',
  };

  const openAddRun = (dnaId, isolateCode) => {
    setRunForm({ dnaId, isolateCode, runId: null, values: { ...EMPTY_RUN_VALUES }, rawFile: null, processedFile: null });
    setRunError('');
    runErrs.reset();
  };

  const openEditRun = (run) => {
    setRunForm({
      dnaId: run.dna_id,
      isolateCode: run.isolate_code,
      runId: run.run_id,
      values: {
        amplification_gene_id: run.amplification_gene_id || '',
        amplification_primers_id: run.amplification_primers_id || '',
        sequencing_primers_id: run.sequencing_primers_id || '',
        date_sent_for_sequencing: (run.date_sent_for_sequencing || '').slice(0, 10),
        sequencing_provider_id: run.sequencing_provider_id || '',
        sequence_quality_id: run.sequence_quality_id || '',
        sequence_length: run.sequence_length ?? '',
        identification_level_id: run.identification_level_id || '',
        identification_confidence_id: run.identification_confidence_id || '',
        identification_blast_result: run.identification_blast_result || '',
        query_coverage: run.query_coverage ?? '',
        notes: run.notes || '',
        platform: run.platform || '',
        barcode: run.barcode || '',
      },
      rawFile: null,
      processedFile: null,
    });
    setRunError('');
    runErrs.reset();
  };

  const handleRunFieldChange = (field, value) => {
    setRunForm(f => ({ ...f, values: { ...f.values, [field]: value } }));
    runErrs.clear(field);
  };

  const submitRunForm = async (e) => {
    e.preventDefault();
    // A new run needs its gene: create_run refuses one without, and the run
    // code is built from it. An edit may leave it as it is.
    const errors = collectErrors([['amplification_gene_id', runForm.runId ? null
      : required(runForm.values.amplification_gene_id, 'runForm.geneRequiredError')]]);
    if (hasErrors(errors)) {
      setRunError('');
      runErrs.show(errors);
      return;
    }
    runErrs.reset();
    setRunSaving(true);
    setRunError('');
    try {
      const payload = { ...runForm.values };
      const selectedGene = geneOptions.find(g => g.id === runForm.values.amplification_gene_id);
      const geneName = selectedGene ? selectedGene.name : '';
      if (runForm.rawFile) {
        const { s3_url, s3_key } = await uploadDnaFile(runForm.rawFile, runForm.dnaId, geneName, 'raw');
        payload.raw_sequence_file_url = s3_url;
        payload.raw_sequence_file_key = s3_key;
      }
      if (runForm.processedFile) {
        const { s3_url, s3_key } = await uploadDnaFile(runForm.processedFile, runForm.dnaId, geneName, 'processed');
        payload.processed_sequence_file_url = s3_url;
        payload.processed_sequence_file_key = s3_key;
      }
      if (runForm.runId) {
        await api.put(`/dna/runs/${runForm.runId}`, payload);
      } else {
        await api.post(`/dna/${runForm.dnaId}/runs`, payload);
      }
      setRunForm(null);
      setNewRunTarget(null);
      load();
    } catch (err) {
      const data = err.response?.data || {};
      // Another run's isolate and gene spell the same code. Named, because
      // retrying cannot fix it: one of the two codes has to change.
      setRunError(data.code === 'run_code_taken'
        ? t('runForm.codeTaken', { code: data.run_code })
        : extractErrorMessage(data, t, t('runForm.saveFailed')));
    } finally {
      setRunSaving(false);
    }
  };

  const flagResequencing = (runId) => {
    if (!window.confirm(t('flagResequencingConfirm'))) return;
    setFlaggingRunId(runId);
    api.put(`/dna/runs/${runId}/flag-resequencing`)
      .then(() => load())
      .catch(err => window.alert(err.response?.data?.error || t('flagResequencingFailed')))
      .finally(() => setFlaggingRunId(null));
  };

  // "12.5 ng/µL · A260/280 1.82 · Freezer B / 3 / 12 / C7": what the extract
  // measured and where its tube is, beside its code. '' when nothing is known.
  const extractSummary = (rec) => [
    rec.concentration_ng_ul != null ? t('extractSummary.concentration', { value: rec.concentration_ng_ul }) : null,
    rec.a260_280 != null ? t('extractSummary.ratio', { value: rec.a260_280 }) : null,
    shortLocation(rec, tSpec),
  ].filter(Boolean).join(' · ');

  // The New Extraction button's handler; the empty page offers the same thing.
  const toggleIsolateForm = () => { setShowForm(s => !s); setFormError(''); createErrs.reset(); };

  // A run's Isolate ID and lab code are its own when GET /dna/runs carries
  // them, else its extraction's: a run descends from exactly one extract.
  const extractOf = Object.fromEntries(records.map(rec => [rec.dna_id, rec]));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
        {canEdit && (
          <button onClick={toggleIsolateForm} style={btnStyle('var(--c-primary)')}
                  title={t('isolateForm.newTip')}>
            {showForm ? t('common:cancel') : t('newIsolateBtn')}
          </button>
        )}
        <ImportLink kind="dna" />
        <ImportLink kind="pitax" />
      </div>

      <ReferencePhylogenyPanel />

      {showForm && (
        <form onSubmit={handleSubmit} style={formStyle} ref={createErrs.containerRef} data-testid="dna-form">
          <h3 ref={isolateHeadingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="dna-form-title">{t('isolateForm.title')}</h3>
          <p style={requiredNoteStyle}>{t('common:validation.requiredNote')}</p>
          {formError && <p role="alert" style={formErrorStyle}>{formError}</p>}
          <div style={gridStyle}>
            <div style={labelStyle} data-field="date_performed">
              <label htmlFor="dna-date_performed">{t('isolateForm.extractionDateLabel')}</label>
              <input id="dna-date_performed" name="date_performed" type="date" value={form.date_performed} onChange={handleChange}
                     aria-required="true" {...invalidProps(errorIdFor('dna-date_performed'), !!createErrs.errors.date_performed)}
                     style={{ ...inputStyle, ...invalidBorder(!!createErrs.errors.date_performed) }} title={t('tip.extractionDate')} />
              <FieldError id={errorIdFor('dna-date_performed')} testId="dna-date_performed-error">
                {validationMessage(t, createErrs.errors.date_performed)}
              </FieldError>
            </div>
            <label style={labelStyle}>
              {t('isolateForm.laboratoryLabel')}
              <input name="laboratory" value={form.laboratory} onChange={handleChange} style={inputStyle} title={t('tip.laboratory')} />
            </label>
            <label style={labelStyle}>
              {t('isolateForm.performedByLabel')}
              <select name="performed_by" value={form.performed_by} onChange={handleChange} style={inputStyle} title={t('tip.performedBy')}>
                <option value="">{t('selectUserPlaceholder')}</option>
                {userList.map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
              </select>
            </label>
            <label style={labelStyle}>
              {t('isolateForm.slantIdLabel')}
              <InvalidWithin invalid={!!createErrs.errors.culture} errorId={errorIdFor('dna-culture')} data-field="culture">
                <RecordAutocomplete
                  fetchUrl="/slants"
                  getValue={s => s.slant_id}
                  getLabel={s => s.slant_code}
                  getSecondary={s => s.taxon_name}
                  value={form.slant_id}
                  onChange={id => { if (!id) setSourceFilled(false); setForm(f => ({ ...f, slant_id: id, plate_id: id ? '' : f.plate_id, sample_id: (id || !sourceFilled) ? f.sample_id : '', external_id: (id || !sourceFilled) ? f.external_id : '' })); }}
                  onSelect={s => { setSourceFilled(true); createErrs.clear('culture'); setForm(f => ({ ...f, slant_id: s.slant_id, plate_id: '', sample_id: s.sample_id || '', external_id: s.external_id || '' })); }}
                  placeholder={t('isolateForm.slantIdPlaceholder')}
                />
              </InvalidWithin>
            </label>
            <label style={labelStyle}>
              {t('isolateForm.plateIdLabel')}
              <InvalidWithin invalid={!!createErrs.errors.culture} errorId={errorIdFor('dna-culture')}>
                <RecordAutocomplete
                  fetchUrl="/plates"
                  getValue={p => p.plate_id}
                  getLabel={p => p.plate_code}
                  getSecondary={p => p.taxon_name}
                  value={form.plate_id}
                  onChange={id => { if (!id) setSourceFilled(false); setForm(f => ({ ...f, plate_id: id, slant_id: id ? '' : f.slant_id, sample_id: (id || !sourceFilled) ? f.sample_id : '', external_id: (id || !sourceFilled) ? f.external_id : '' })); }}
                  onSelect={p => { setSourceFilled(true); createErrs.clear('culture'); setForm(f => ({ ...f, plate_id: p.plate_id, slant_id: '', sample_id: p.sample_id || '', external_id: p.external_id || '' })); }}
                  placeholder={t('isolateForm.plateIdPlaceholder')}
                />
              </InvalidWithin>
            </label>
            <p style={{ gridColumn: '1 / -1', fontSize: 13, color: 'var(--c-text-muted-fg)', margin: 0 }}
               data-testid="dna-source-hint">
              {t('isolateForm.cultureHint')}
            </p>
            {createErrs.errors.culture && (
              <div style={{ gridColumn: '1 / -1', marginTop: -10 }}>
                <FieldError id={errorIdFor('dna-culture')} testId="dna-culture-error">
                  {validationMessage(t, createErrs.errors.culture)}
                </FieldError>
              </div>
            )}
            {dnaExistsForSlant && (
              <div style={{ gridColumn: '1 / -1', color: 'var(--c-danger-fg)', fontSize: 13 }}>
                {t('isolateForm.dnaExistsForSlantWarning')}
              </div>
            )}
            {dnaExistsForPlate && (
              <div style={{ gridColumn: '1 / -1', color: 'var(--c-danger-fg)', fontSize: 13 }}>
                {t('isolateForm.dnaExistsForPlateWarning')}
              </div>
            )}
            <ExtractFields values={form} onChange={handleChange} testIdPrefix="dna" t={t} tSpec={tSpec} />
            <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
              {t('isolateForm.notesLabel')}
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} style={inputStyle} title={t('tip.notes')} />
            </label>
          </div>
          {/* Enabled while only a required field is missing -- pressing it
              names the field. A culture that already has its DNA record keeps
              it disabled: that has its own warning beside the pickers. */}
          <button type="submit" disabled={submitting || dnaBlocked} data-testid="dna-create"
                  title={t('isolateForm.createTip')}
                  style={btnStyle(dnaBlocked ? 'var(--c-border-strong)' : 'var(--c-primary)')}>
            {submitting ? t('common:saving') : t('isolateForm.createBtn')}
          </button>
        </form>
      )}

      <CreatedCodes codes={createdCodes} onDismiss={() => setCreatedCodes([])} />

      {canEdit && newRunTarget && !runForm && (
        <div style={{ ...formStyle, borderColor: 'var(--c-primary)' }}>
          <p style={{ margin: '0 0 10px' }}>{t('isolateForm.createdIsolateCode', { code: newRunTarget.isolateCode })}</p>
          <button type="button" onClick={() => openAddRun(newRunTarget.dnaId, newRunTarget.isolateCode)} style={btnStyle('var(--c-primary)')} title={t('tip.addRun')}>
            {t('addRunBtn')}
          </button>
        </div>
      )}

      {!loading && !runsLoading && records.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {records.map(rec => (
            <div key={rec.dna_id} ref={el => { rowRefs.current[`dna:${rec.dna_id}`] = el; }}
              style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '100%', alignItems: 'center', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '2px 2px 2px 10px', background: highlight?.dnaId === rec.dna_id ? 'var(--c-warning-soft)' : undefined }}>
              <span dir="ltr" style={{ fontSize: 12, marginInlineEnd: 8 }}>{rec.isolate_code}</span>
              {rec.specimen_code && (
                <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginInlineEnd: 8 }}
                      title={t('tip.isolateId')} data-testid="dna-specimen-code">
                  {t('isolateId')}: <bdi dir="ltr" style={{ fontFamily: 'monospace' }}>{rec.specimen_code}</bdi>
                </span>
              )}
              {rec.lab_code && (
                <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginInlineEnd: 8 }}
                      title={t('tip.labCode')} data-testid="dna-lab-code">
                  {t('labCode')}: <bdi dir="ltr" style={{ fontFamily: 'monospace' }}>{rec.lab_code}</bdi>
                </span>
              )}
              {extractSummary(rec) && (
                // Numbers, units and a place in Latin script: read left to
                // right in Hebrew too, or "25.4 ng/µL" comes apart.
                <span dir="ltr" style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginInlineEnd: 8 }}
                      title={[t('tip.extractSummary'), rec.storage_unit && describeLocation(rec, tSpec)]
                        .filter(Boolean).join(' ')}
                      data-testid="dna-extract-summary">
                  {extractSummary(rec)}
                </span>
              )}
              {/* What sequencing concluded (PITAX), newest standing answer first;
                  the button opens every answer below the list. */}
              <button type="button" onClick={() => setIdsFor(id => (id === rec.dna_id ? null : rec.dna_id))}
                aria-expanded={idsFor === rec.dna_id} aria-controls={`dna-identifications-${rec.dna_id}`}
                title={t('tip.identifications')} data-testid="dna-identifications-btn"
                aria-label={rec.latest_identification
                  ? `${t('identifications.btn')}: ${latestSummary(rec.latest_identification, t)}` : undefined}
                style={{ background: 'none', border: 'none', borderInlineStart: '1px solid var(--c-border-strong)', padding: '4px 8px', cursor: 'pointer', fontSize: 12, maxWidth: '100%', textAlign: 'start', overflowWrap: 'anywhere' }}>
                {rec.latest_identification ? (
                  <LatestIdentification latest={rec.latest_identification} t={t} />
                ) : t('identifications.btn')}
              </button>
              {rec.isolate_code && (
                <button type="button" onClick={() => setQrView(rec.isolate_code)} title={t('tip.qrExtraction')}
                  style={{ background: 'none', border: 'none', borderInlineStart: '1px solid var(--c-border-strong)', padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
                  {t('qrBtn')}
                </button>
              )}
              {canEdit && (
                <button type="button" onClick={() => openAddRun(rec.dna_id, rec.isolate_code)} title={t('tip.addRun')}
                  style={{ background: 'none', border: 'none', borderInlineStart: '1px solid var(--c-border-strong)', padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>
                  {t('addRunBtn')}
                </button>
              )}
              {canEdit && (
                <button type="button" onClick={() => openEdit(rec)} title={t('tip.editExtraction')}
                  style={{ background: 'var(--c-info)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 4, padding: '3px 10px', marginInlineStart: 4, cursor: 'pointer', fontSize: 12 }}>
                  {t('common:edit')}
                </button>
              )}
              {canDelete && (
                <button type="button" onClick={() => { setDeleteError(''); setDeleteTarget(rec.dna_id); }} title={t('tip.deleteExtraction')}
                  style={{ background: 'var(--c-danger)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 4, padding: '3px 10px', marginInlineStart: 4, marginInlineEnd: 4, cursor: 'pointer', fontSize: 12 }}>
                  {t('common:delete')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {idsFor && records.some(r => r.dna_id === idsFor) && (
        <DnaIdentificationsPanel dnaId={idsFor} canEdit={!!canEdit}
          isolateCode={records.find(r => r.dna_id === idsFor)?.isolate_code}
          onClose={closeIdentifications} onChanged={refreshRecords} />
      )}

      {navNotFound && (
        <p style={{ background: 'var(--c-info-soft-2)', color: 'var(--c-info-3-fg)', border: '1px solid var(--c-info-border-2)', borderRadius: 6, padding: '6px 10px', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>{t('notFoundNav')}</span>
          <button type="button" onClick={() => setNavNotFound(false)} title={t('tip.dismissMessage')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, color: 'inherit' }}>×</button>
        </p>
      )}

      {(loading || runsLoading) && (
        <OrganicLoader block motif="genetic" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}
      {error && <p role="alert" style={{ ...formErrorStyle, margin: '0 0 12px' }}>{error}</p>}

      {/* Nothing extracted yet. Not shown while the New Extraction form is
          open: that is already the thing it would suggest. */}
      {!loading && !runsLoading && !error && records.length === 0 && runs.length === 0 && !showForm && (
        <OrganicEmptyState kind="dna" title={t('noRecordsYet')} hint={t('empty.hint')}
          action={canEdit ? (
            <button type="button" onClick={toggleIsolateForm} style={btnStyle('var(--c-primary)')}
                    data-testid="dna-empty-create" title={t('isolateForm.newTip')}>
              {t('empty.action')}
            </button>
          ) : null} />
      )}

      {!loading && !error && records.length > 0 && (
        <button type="button" onClick={() => setTableExpanded(e => !e)} title={t('tip.toggleTable')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 600, padding: '4px 0', marginBottom: 8 }}>
          {tableExpanded ? t('table.hideRecords', { count: records.length }) : t('table.showRecords', { count: records.length })}
        </button>
      )}

      {/* Extractions exist but none has been sequenced: say so, and -- to
          whoever may add one -- where a run is added (each extraction above
          has its own button; nobody else has those buttons). */}
      {!loading && !runsLoading && !error && tableExpanded && (records.length > 0 || runs.length > 0) && (
        runs.length === 0 ? (
          <OrganicEmptyState kind="dna" compact title={t('empty.noRunsTitle')}
            hint={canEdit ? t('empty.noRunsHint') : undefined} />
        ) : (
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1900 }}>
            <thead>
              <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                {[
                  t('isolateId'), t('labCode'),
                  t('table.runCode'), t('table.isolateCode'), t('table.bankSlants'), t('table.extractionDate'),
                  t('table.gene'), t('table.amplificationPrimers'), t('table.sequencingPrimers'),
                  t('table.dateSent'), t('table.provider'), t('table.platform'),
                  t('table.rawFile'), t('table.quality'), t('table.processedFile'), t('table.length'),
                  t('table.level'), t('table.confidence'), t('table.blastResult'), t('table.queryCoverage'),
                  t('table.notes'),
                  ...((canEdit || canDelete) ? [t('table.actions')] : []),
                ].map(h => <th key={h} style={thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.run_id} ref={el => { rowRefs.current[`run:${r.run_id}`] = el; }}
                  style={{ borderBottom: '1px solid var(--c-bg-muted-3)', background: highlight?.runIds.includes(r.run_id) ? 'var(--c-warning-soft)' : undefined }}>
                  <CodeCell code={r.specimen_code ?? extractOf[r.dna_id]?.specimen_code} testId="dna-run-specimen-code" />
                  <CodeCell code={r.lab_code ?? extractOf[r.dna_id]?.lab_code} testId="dna-run-lab-code" />
                  <td style={{ ...tdStyle, fontWeight: 600 }} dir="ltr">
                    {r.run_code || '—'}
                    {r.run_code && (
                      <button type="button" onClick={() => setQrView(r.run_code)} title={t('tip.qrRun')}
                        style={{ background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 4, padding: '1px 6px', marginInlineStart: 6, cursor: 'pointer', fontSize: 11, fontWeight: 400 }}>
                        {t('qrBtn')}
                      </button>
                    )}
                  </td>
                  <td style={tdStyle} dir="ltr">{r.isolate_code || '—'}</td>
                  <td style={tdStyle} dir="ltr">
                    {(r.bank_slants || []).length === 0
                      ? '—'
                      : r.bank_slants.map(b => (
                          <div key={b.slant_id}>
                            {b.slant_code}
                            {b.relation !== 'source' && (
                              <span style={{ color: 'var(--c-text-muted-fg)', fontSize: 11 }}> ({t(`bankRelation.${b.relation}`)})</span>
                            )}
                          </div>
                        ))}
                  </td>
                  <td style={tdStyle}>{r.extraction_date ? new Date(r.extraction_date).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{r.gene_name || '—'}</td>
                  <td style={tdStyle}>{r.amplification_primers_name || '—'}</td>
                  <td style={tdStyle}>{r.sequencing_primers_name || '—'}</td>
                  <td style={tdStyle}>{r.date_sent_for_sequencing ? new Date(r.date_sent_for_sequencing).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{r.provider_name || '—'}</td>
                  <td style={tdStyle} data-testid="dna-run-platform-cell">
                    {r.platform ? t(`platforms.${r.platform}`) : '—'}
                    {r.barcode && (
                      <div dir="ltr" style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{r.barcode}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {/* Re-based: a desktop file URL names 127.0.0.1, the wrong machine to a phone. */}
                    {r.raw_sequence_file_url
                      ? <a href={resolvePhotoUrl(r.raw_sequence_file_url)} target="_blank" rel="noreferrer" title={t('tip.openRawFile')}
                           data-testid="dna-run-raw-link" style={{ color: 'var(--c-primary-fg)' }}>{t('viewLink')}</a>
                      : '—'}
                  </td>
                  <td style={tdStyle}>{r.quality_name || '—'}</td>
                  <td style={tdStyle}>
                    {r.processed_sequence_file_url
                      ? <a href={resolvePhotoUrl(r.processed_sequence_file_url)} target="_blank" rel="noreferrer" title={t('tip.openProcessedFile')}
                           data-testid="dna-run-processed-link" style={{ color: 'var(--c-primary-fg)' }}>{t('viewLink')}</a>
                      : '—'}
                  </td>
                  <td style={tdStyle}>{r.sequence_length ?? '—'}</td>
                  <td style={tdStyle}>{r.level_name || '—'}</td>
                  <td style={tdStyle}>{r.confidence_name || '—'}</td>
                  <td style={{ ...tdStyle, fontStyle: 'italic' }} dir="ltr">{r.identification_blast_result || '—'}</td>
                  <td style={tdStyle}>{r.query_coverage != null ? `${r.query_coverage}%` : '—'}</td>
                  <td style={tdStyle}>
                    {r.notes || '—'}
                    {r.needs_resequencing && (
                      <span style={{ display: 'block', marginTop: 4, background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        {t('needsResequencingBadge')}
                      </span>
                    )}
                  </td>
                  {(canEdit || canDelete) && (
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {canEdit && (
                        <button onClick={() => openEditRun(r)} title={t('tip.editRun')} style={{ background: 'var(--c-info)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
                          {t('common:edit')}
                        </button>
                      )}
                      {' '}
                      {canEdit && !r.needs_resequencing && (
                        <button onClick={() => flagResequencing(r.run_id)} disabled={flaggingRunId === r.run_id} title={t('tip.flagResequencing')}
                          style={{ background: 'var(--c-warning-text-2)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>
                          {t('flagResequencingBtn')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}

      {editTarget && (
        <div style={overlayStyle} onClick={() => setEditTarget(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 560 }} onClick={e => e.stopPropagation()}>
            <h3 ref={editHeadingRef} tabIndex={-1} style={{ marginTop: 0 }}>{t('isolateForm.editTitle')}</h3>
            <p style={requiredNoteStyle}>{t('common:validation.requiredNote')}</p>
            {editError && <p role="alert" style={formErrorStyle}>{editError}</p>}
            <form onSubmit={submitEdit} ref={editErrs.containerRef} data-testid="dna-edit-form">
              <div style={gridStyle}>
                <div style={labelStyle} data-field="date_performed">
                  <label htmlFor="dna-edit-date_performed">{t('isolateForm.extractionDateLabel')}</label>
                  <input id="dna-edit-date_performed" name="date_performed" type="date" value={editTarget.form.date_performed} onChange={handleEditChange}
                         aria-required="true" {...invalidProps(errorIdFor('dna-edit-date_performed'), !!editErrs.errors.date_performed)}
                         style={{ ...inputStyle, ...invalidBorder(!!editErrs.errors.date_performed) }} title={t('tip.extractionDate')} />
                  <FieldError id={errorIdFor('dna-edit-date_performed')} testId="dna-edit-date_performed-error">
                    {validationMessage(t, editErrs.errors.date_performed)}
                  </FieldError>
                </div>
                <label style={labelStyle}>
                  {t('isolateForm.laboratoryLabel')}
                  <input name="laboratory" value={editTarget.form.laboratory} onChange={handleEditChange} style={inputStyle} title={t('tip.laboratory')} />
                </label>
                <label style={labelStyle}>
                  {t('isolateForm.performedByLabel')}
                  <select name="performed_by" value={editTarget.form.performed_by} onChange={handleEditChange} style={inputStyle} title={t('tip.performedBy')}>
                    <option value="">{t('selectUserPlaceholder')}</option>
                    {userList.map(u => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  {t('isolateForm.donorNameLabel').replace(' *', '')}
                  <input name="donor_name" value={editTarget.form.donor_name} onChange={handleEditChange} style={inputStyle} title={t('tip.donorName')} />
                </label>
                <label style={labelStyle}>
                  {t('isolateForm.donorContactLabel')}
                  <input name="donor_contact" value={editTarget.form.donor_contact} onChange={handleEditChange} style={inputStyle} title={t('tip.donorContact')} />
                </label>
                <label style={labelStyle}>
                  {t('isolateForm.receivedDateLabel')}
                  <input name="received_date" type="date" value={editTarget.form.received_date} onChange={handleEditChange} style={inputStyle} title={t('tip.receivedDate')} />
                </label>
                <ExtractFields values={editTarget.form} onChange={handleEditChange} testIdPrefix="dna-edit" t={t} tSpec={tSpec} />
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  {t('isolateForm.notesLabel')}
                  <textarea name="notes" value={editTarget.form.notes} onChange={handleEditChange} rows={2} style={inputStyle} title={t('tip.notes')} />
                </label>
              </div>
                {canAssign && (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--c-border)', paddingTop: 12 }}>
                    <strong style={{ fontSize: 13 }}>{t('editModal.assignedToTitle')}</strong>
                    {assignmentsLoading ? (
                      <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 12 }}>
                        <OrganicLoader inline showLabel announce={false} label={t('common:loading')} slowLabel={t('common:stillWorking')} />
                      </p>
                    ) : (
                      <div style={{ marginTop: 6 }}>
                        {dnaAssignments.length === 0 ? (
                          <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 12 }}>{t('editModal.noAssignments')}</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                            {dnaAssignments.map(a => (
                              <div key={a.assignment_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--c-bg-muted)', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                                <span>{a.username}</span>
                                <button type="button" onClick={() => removeDnaAssignment(a.assignment_id)} title={t('tip.removeAssignment')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 13 }}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {assignError && <p role="alert" style={{ ...formErrorStyle, fontSize: 12, margin: 0 }}>{assignError}</p>}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                          <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)} title={t('tip.assignUser')} style={{ ...inputStyle, flex: 1, fontSize: 12 }}>
                            <option value="">{t('editModal.assignSelectPlaceholder')}</option>
                            {allUsers.map(u => (
                              <option key={u.user_id} value={u.user_id}>{u.username}</option>
                            ))}
                          </select>
                          <button type="button" onClick={submitDnaAssignment} disabled={assignSaving || !assignUserId} title={t('tip.assignBtn')} style={{ background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                            {assignSaving ? t('common:saving') : t('editModal.assignBtn')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" disabled={editSaving} style={btnStyle('var(--c-primary)')} title={t('tip.saveEdit')}>
                  {editSaving ? t('common:saving') : t('common:save')}
                </button>
                <button type="button" onClick={() => setEditTarget(null)} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelEdit')}>{t('common:cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {runForm && (
        <div style={overlayStyle} onClick={() => setRunForm(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
            <h3 ref={runHeadingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="dna-run-title">
              {runForm.runId ? t('runForm.editTitle') : t('runForm.title', { isolateCode: runForm.isolateCode })}
            </h3>
            {!runForm.runId && <p style={requiredNoteStyle}>{t('common:validation.requiredNote')}</p>}
            {runError && <p role="alert" style={formErrorStyle}>{runError}</p>}
            <form onSubmit={submitRunForm} ref={runErrs.containerRef} data-testid="dna-run-form">
              <div style={gridStyle}>
                {/* The message sits under the <label>, outside the name it gives the select. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={labelStyle}>
                    {t('runForm.geneLabel')}{!runForm.runId ? ' *' : ''}
                    <InvalidWithin required={!runForm.runId} invalid={!!runErrs.errors.amplification_gene_id} errorId={errorIdFor('dna-run-gene')}
                                   data-field="amplification_gene_id">
                      <ReferenceSelect category="genes" value={runForm.values.amplification_gene_id}
                        onChange={id => handleRunFieldChange('amplification_gene_id', id)} />
                    </InvalidWithin>
                    {(() => {
                      const g = geneOptions.find(o => o.id === runForm.values.amplification_gene_id);
                      return g && !runForm.runId ? (
                        <span dir="ltr" style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', fontWeight: 400 }}>
                          {/* -G- is main's run-code scheme; the colour is this branch's token. */}
                          {t('runForm.codeHint', { code: `${runForm.isolateCode}-G-${g.name}` })}
                        </span>
                      ) : null;
                    })()}
                  </label>
                  <FieldError id={errorIdFor('dna-run-gene')} testId="dna-run-gene-error">
                    {validationMessage(t, runErrs.errors.amplification_gene_id)}
                  </FieldError>
                </div>
                <label style={labelStyle}>
                  {t('runForm.amplificationPrimersLabel')}
                  <ReferenceSelect category="primers" value={runForm.values.amplification_primers_id}
                    onChange={id => handleRunFieldChange('amplification_primers_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.sequencingPrimersLabel')}
                  <ReferenceSelect category="primers" value={runForm.values.sequencing_primers_id}
                    onChange={id => handleRunFieldChange('sequencing_primers_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.dateSentLabel')}
                  <input type="date" value={runForm.values.date_sent_for_sequencing}
                    onChange={e => handleRunFieldChange('date_sent_for_sequencing', e.target.value)} style={inputStyle} title={t('tip.dateSent')} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.providerLabel')}
                  <ReferenceSelect category="providers" value={runForm.values.sequencing_provider_id}
                    onChange={id => handleRunFieldChange('sequencing_provider_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.platformLabel')}
                  <select value={runForm.values.platform} data-testid="dna-run-platform"
                    onChange={e => handleRunFieldChange('platform', e.target.value)} style={inputStyle} title={t('tip.platform')}>
                    <option value="">{t('runForm.noPlatform')}</option>
                    {PLATFORMS.map(p => <option key={p} value={p}>{t(`platforms.${p}`)}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  {t('runForm.barcodeLabel')}
                  <input value={runForm.values.barcode} maxLength={60} dir="ltr" autoComplete="off" spellCheck={false}
                    data-testid="dna-run-barcode" onChange={e => handleRunFieldChange('barcode', e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace' }} title={t('tip.barcode')} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.rawFileLabel')}
                  <input type="file" accept={RAW_FILE_ACCEPT} data-testid="dna-run-raw-file"
                    onChange={e => setRunForm(f => ({ ...f, rawFile: e.target.files[0] || null }))} style={inputStyle} title={t('tip.rawFile')} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.qualityLabel')}
                  <ReferenceSelect category="quality-options" value={runForm.values.sequence_quality_id}
                    onChange={id => handleRunFieldChange('sequence_quality_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.processedFileLabel')}
                  <input type="file" accept={PROCESSED_FILE_ACCEPT} data-testid="dna-run-processed-file"
                    onChange={e => setRunForm(f => ({ ...f, processedFile: e.target.files[0] || null }))} style={inputStyle} title={t('tip.processedFile')} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.lengthLabel')}
                  <input type="number" min="0" step="1" value={runForm.values.sequence_length}
                    onChange={e => handleRunFieldChange('sequence_length', e.target.value)} style={inputStyle} title={t('tip.sequenceLength')} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.levelLabel')}
                  <ReferenceSelect category="identification-levels" value={runForm.values.identification_level_id}
                    onChange={id => handleRunFieldChange('identification_level_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.confidenceLabel')}
                  <ReferenceSelect category="confidence-levels" value={runForm.values.identification_confidence_id}
                    onChange={id => handleRunFieldChange('identification_confidence_id', id)} />
                </label>
                <label style={labelStyle}>
                  {t('runForm.queryCoverageLabel')}
                  <input type="number" min="0" max="100" step="0.01" value={runForm.values.query_coverage}
                    onChange={e => handleRunFieldChange('query_coverage', e.target.value)} style={inputStyle} title={t('tip.queryCoverage')} />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  {t('runForm.blastResultLabel')}
                  <input value={runForm.values.identification_blast_result} dir="ltr"
                    onChange={e => handleRunFieldChange('identification_blast_result', e.target.value)} style={inputStyle} title={t('tip.blastResult')} />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  {t('runForm.notesLabel')}
                  <textarea rows={2} value={runForm.values.notes}
                    onChange={e => handleRunFieldChange('notes', e.target.value)} style={inputStyle} title={t('tip.runNotes')} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" disabled={runSaving} style={btnStyle('var(--c-primary)')} title={t('tip.saveRun')}>
                  {runSaving ? t('common:saving') : t('runForm.saveBtn')}
                </button>
                <button type="button" onClick={() => setRunForm(null)} style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelRun')}>{t('common:cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {qrView && (
        <div style={overlayStyle} onClick={() => setQrView(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 300 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px' }}>{t('qrTitle')}</h3>
            <QRCodeLabel value={qrView} size={180} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={() => setQrView(null)} style={btnStyle('var(--c-text-muted)')} title={t('tip.closeQr')}>{t('common:cancel')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title={t('deleteConfirmTitle')}
        deleting={deletingRecord}
        error={deleteError}
        onConfirm={confirmDeleteRecord}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

/*
 * The extract's own details (migration 0050), the same in the create form and
 * the edit dialog: the kit and its lot, what was measured, and where the tube
 * is kept. Each change goes to the form's own handler as a named input event.
 */
function ExtractFields({ values, onChange, testIdPrefix, t, tSpec }) {
  const field = (name) => ({ name, value: values[name], onChange, 'data-testid': `${testIdPrefix}-${name}` });
  // "c 7" is C7 to the server too; written so here as it is typed.
  const onWell = (e) => onChange({ target: { name: 'storage_well', value: e.target.value.toUpperCase().replace(/\s+/g, '') } });
  // A well is a place only inside a unit: taking the tube out of storage
  // empties the rest of its place, so no freezer is left claiming it.
  const inUnit = !!values.storage_unit;
  // The parts of a place, faded while there is no unit for them to be in.
  const placeInput = inUnit ? inputStyle : { ...inputStyle, opacity: 0.55 };
  const onUnit = (e) => {
    onChange(e);
    if (!e.target.value) PLACE_PARTS.forEach(name => onChange({ target: { name, value: '' } }));
  };
  return (
    <>
      <label style={labelStyle}>
        {t('isolateForm.extractionKitLabel')}
        <input {...field('extraction_kit')} maxLength={120} style={inputStyle} title={t('tip.extractionKit')} />
      </label>
      <label style={labelStyle}>
        {t('isolateForm.extractionLotLabel')}
        <input {...field('extraction_lot')} maxLength={60} dir="ltr" style={inputStyle} title={t('tip.extractionLot')} />
      </label>
      <label style={labelStyle}>
        {t('isolateForm.concentrationLabel')}
        <input {...field('concentration_ng_ul')} type="number" min="0" max="100000" step="any" inputMode="decimal"
               style={inputStyle} title={t('tip.concentration')} />
      </label>
      <label style={labelStyle}>
        {t('isolateForm.a260280Label')}
        <input {...field('a260_280')} type="number" min="0" max="5" step="any" inputMode="decimal"
               style={inputStyle} title={t('tip.a260280')} />
      </label>
      <fieldset style={fieldsetStyle}>
        <legend style={{ fontSize: 14, fontWeight: 500, padding: '0 4px' }}>{t('isolateForm.tubeLocationTitle')}</legend>
        <div style={locationGridStyle}>
          <label style={labelStyle}>
            {t('isolateForm.storageUnitLabel')}
            <select {...field('storage_unit')} onChange={onUnit} style={inputStyle} title={t('tip.storageUnit')}>
              <option value="">{t('isolateForm.notStored')}</option>
              {STORAGE_UNITS.map(u => <option key={u} value={u}>{tSpec(`storage.units.${u}`)}</option>)}
            </select>
          </label>
          <label style={labelStyle}>
            {t('isolateForm.freezerIdLabel')}
            <input {...field('storage_unit_label')} maxLength={60} placeholder={t('isolateForm.freezerIdPlaceholder')}
                   disabled={!inUnit} style={placeInput} title={t('tip.freezerId')} />
          </label>
          <label style={labelStyle}>
            {t('isolateForm.rackLabel')}
            <input {...field('storage_rack')} maxLength={60} dir="ltr" disabled={!inUnit} style={placeInput}
                   title={t('tip.rack')} />
          </label>
          <label style={labelStyle}>
            {t('isolateForm.boxLabel')}
            <input {...field('storage_box')} maxLength={60} dir="ltr" disabled={!inUnit} style={placeInput}
                   title={t('tip.box')} />
          </label>
          <label style={labelStyle}>
            {t('isolateForm.wellLabel')}
            <input {...field('storage_well')} onChange={onWell} maxLength={5} dir="ltr" autoCapitalize="characters"
                   autoComplete="off" spellCheck={false} disabled={!inUnit}
                   style={{ ...placeInput, fontFamily: 'monospace', textTransform: 'uppercase' }} title={t('tip.well')} />
          </label>
        </div>
      </fieldset>
    </>
  );
}

const thStyle = { padding: '8px 12px', textAlign: 'start', fontWeight: 600 };
const tdStyle = { padding: '8px 12px' };
const codeStyle = { fontFamily: 'monospace', fontSize: 12 };

// An isolate's code in a table cell: monospace, left to right in Hebrew too,
// and '—' when there is none.
function CodeCell({ code, testId }) {
  return (
    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} data-testid={testId}>
      {code ? <bdi dir="ltr" style={codeStyle}>{code}</bdi> : '—'}
    </td>
  );
}

// A refusal said once, in the theme's danger ink, and announced: focus stays
// on the button that was pressed, so nothing else would say it.
const formErrorStyle = { color: 'var(--c-danger-fg)', margin: '0 0 12px' };
const requiredNoteStyle = { margin: '0 0 12px', fontSize: 12, color: 'var(--c-text-muted-fg)' };

const formStyle = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const gridStyle = {
  display: 'grid',
  // min(): one column on a phone, never one wider than the form. A field
  // that spans the row says '1 / -1', which spans the columns there are;
  // 'span 2' would make a second one off the edge of the screen.
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
  gap: 14,
  marginBottom: 16,
};

const fieldsetStyle = {
  gridColumn: '1 / -1',
  border: '1px solid var(--c-border)',
  borderRadius: 6,
  margin: 0,
  padding: '8px 12px 12px',
  minWidth: 0,
};

const locationGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: 10,
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  fontWeight: 500,
};

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
};

const btnStyle = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 14,
});

// A dialog taller than the window used to be centred by the overlay, which
// pushed its top (title, close button, first field) above the screen where
// nothing could scroll to it. The overlay scrolls now, and margin:auto on
// the dialog centres it only while it fits.
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'flex-start',
  overflowY: 'auto',
  padding: '24px 16px',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'var(--c-surface)',
  margin: 'auto',
  borderRadius: 10,
  padding: 28,
  width: 440,
  maxWidth: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
